import * as action from 'actions';
import * as onlineStoreAction from 'actions/onlineStore';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import * as onlineStoreApi from 'api/onlineStore';
import * as actNotif from 'actions/notifications';
import {
  ONLINE_STORE_START_SYNCING_PRODUCT_FLOW,
  ONLINE_STORE_START_SYNCING_PRODUCT_VALIDATION_FLOW,
  ONLINE_STORE_SHOP_DETAIL_PRODUCTS_REQUEST,
  ONLINE_STORE_START_POST_SYNCING_PRODUCT_FLOW,
  ONLINE_STORE_CLOSE_SYNCING_PRODUCT_DONE_MODAL,
  ONLINE_STORE_SHOW_SYNCING_PRODUCT_IN_SYNC_MODAL,
} from 'actions/onlineStore/constants';
import { ONLINE_STORE_CHANNEL, UI_FLAGS } from 'utils/onlineStoreUtils';
import * as fbClevertap from 'clevertap/onlineStore/facebook';
import * as googleClevertap from 'clevertap/onlineStore/google';

const clevertapMap = {
  [ONLINE_STORE_CHANNEL.FACEBOOK]: {
    success: fbClevertap.fbUploadProductsSuccess,
    failure: fbClevertap.fbUploadProductsFailure,
  },
  [ONLINE_STORE_CHANNEL.GOOGLE]: {
    success: googleClevertap.googleUploadProductsSuccess,
    failure: googleClevertap.googleUploadProductsFailure,
  },
};

export function* startSyncingProductValidationFlow({ payload }) {
  const { shopId, viewOnly } = payload;
  let customViewOnly = viewOnly;

  yield put(action.showLoadingSpinner(true));

  try {
    const shopDetail = yield getShop(shopId);
    yield put(onlineStoreAction.setShopDetail(shopDetail));

    const { item_stats, item_start_sync_at } = shopDetail;

    if (item_stats.total_items === 0) {
      yield put(onlineStoreAction.showSyncingProductEmptyProductModal());
      return;
    }

    if (item_start_sync_at === null) {
      customViewOnly = false;
    }

    yield put(
      onlineStoreAction.startSyncingProductFlow(
        shopId,
        customViewOnly,
        shopDetail
      )
    );
  } catch (e) {
    yield put(actNotif.error('Please try again later.'));
  } finally {
    yield put(action.showLoadingSpinner(false));
  }
}

export function* pollShopItemSyncStatus() {
  const shopId = yield select(state => state.onlineStore.shop.shopDetail.id);
  let delayCounter = 0;

  while (true) {
    const shopDetail = yield getShop(shopId, false);
    const { item_sync_status } = shopDetail;

    const isPopupOpened = yield select(
      state => state.onlineStore.productSyncing.showSyncingProductInSyncModal
    );
    if (!isPopupOpened) {
      break;
    }

    if (item_sync_status === 'DONE') {
      yield put(onlineStoreAction.startSyncingProductFlow(shopId, true));
    }

    // set delay time to be quicker on first time in case of sync is already done before pusher is yet subscribed
    yield delay(delayCounter < 3 ? 1000 : 5000, true);
    delayCounter++;
  }
}

export function* startSyncingProductFlow({ payload }) {
  yield put(onlineStoreAction.closeSyncingProductEmptySyncedModal());
  yield put(onlineStoreAction.closeSyncingProductInSyncModal());
  yield put(onlineStoreAction.closeSyncingProductResultModal());
  yield put(onlineStoreAction.setShopDetailProducts(0, true, [], 0));
  yield put(action.showLoadingSpinner(true));

  const { shopId, viewOnly } = payload;
  let { shopDetail } = payload;

  try {
    if (!shopDetail) {
      shopDetail = yield getShop(shopId);
      yield put(onlineStoreAction.setShopDetail(shopDetail));
    }

    const clevertap = clevertapMap[shopDetail.channel];
    let { item_sync_status } = shopDetail;

    if (!viewOnly && (!item_sync_status || item_sync_status === 'DONE')) {
      try {
        yield fetch(onlineStoreApi.syncProducts, { shopId });
        clevertap.success(shopDetail.item_stats.total_items);
      } catch (error) {
        clevertap.failure(shopDetail.item_stats.total_items, error.message);
        throw error;
      }

      shopDetail = yield getShop(shopId);
      item_sync_status = shopDetail.item_sync_status;
      yield put(onlineStoreAction.setShopDetail(shopDetail));
    }

    yield put(onlineStoreAction.shopDetailProductsRequest(shopId, 1));

    if (item_sync_status === 'DONE') {
      yield put(onlineStoreAction.showSyncingProductResultModal());
    } else {
      yield put(onlineStoreAction.showSyncingProductInSyncModal());
    }

    yield put(action.showLoadingSpinner(false));
  } catch (e) {
    yield put(onlineStoreAction.closeSyncingProductInSyncModal());
    yield put(actNotif.error('Please try again later.'));
  } finally {
    yield put(action.showLoadingSpinner(false));
  }
}

export function* startPostSyncingProductFlow() {
  const shop = yield select(state => state.onlineStore.shop.shopDetail);
  const shown = shop.ui_flags[UI_FLAGS.PRODUCT_SYNCS_COMPLETED_SHOWN];

  yield put(onlineStoreAction.closeSyncingProductResultModal());
  yield put(onlineStoreAction.pageRequest(shop.channel));

  if (!shown) {
    yield put(onlineStoreAction.showSyncingProductDoneModal());
  }
}

export function* updateSyncProductDoneUiFlag() {
  const shopId = yield select(state => state.onlineStore.shop.shopDetail.id);

  try {
    yield call(onlineStoreApi.updateShopUiFlags, shopId, {
      [UI_FLAGS.PRODUCT_SYNCS_COMPLETED_SHOWN]: true,
    });
  } catch (error) {
    // silent error for updating flags, as it's not crucial
  }
}

export function* shopDetailProductsRequest({ payload }) {
  try {
    yield put(action.showProgressBar());

    if (payload.clearAll) {
      yield put(onlineStoreAction.setShopDetailProducts(0, true, [], 0));
    }

    const { shopDetailProducts, shopDetail } = yield select(
      state => state.onlineStore.shop
    );

    const { shopId, page, status } = payload;
    const shopProducts = yield getShopDetailProducts(
      shopId,
      page,
      status,
      shopDetail.item_sync_status
    );
    const data = payload.clearAll
      ? shopProducts.items
      : shopDetailProducts.data.concat(shopProducts.items);

    yield put(
      onlineStoreAction.setShopDetailProducts(
        page,
        shopProducts.completed,
        data,
        shopProducts.total_item_variant
      )
    );
  } catch (error) {
    yield put(actNotif.error('Please try again later.'));
  } finally {
    yield put(action.hideProgressBar());
  }
}

function* fetch(action, params) {
  const response = yield call(action, params);
  const { status, data } = response;
  if (status !== 200) {
    throw response;
  }

  return data;
}

function* getShop(shopId, itemStats = true) {
  return yield fetch(onlineStoreApi.getShopById, {
    shopId,
    itemStats,
  });
}

function* getShopDetailProducts(shopId, page, status, itemSyncStatus) {
  return yield fetch(onlineStoreApi.getProducts, {
    shopId,
    page,
    status,
    itemSyncStatus,
  });
}

export default function* saga() {
  yield takeLatest(
    ONLINE_STORE_START_SYNCING_PRODUCT_VALIDATION_FLOW,
    startSyncingProductValidationFlow
  );
  yield takeLatest(
    ONLINE_STORE_START_SYNCING_PRODUCT_FLOW,
    startSyncingProductFlow
  );
  yield takeLatest(
    ONLINE_STORE_SHOP_DETAIL_PRODUCTS_REQUEST,
    shopDetailProductsRequest
  );
  yield takeLatest(
    ONLINE_STORE_START_POST_SYNCING_PRODUCT_FLOW,
    startPostSyncingProductFlow
  );
  yield takeLatest(
    ONLINE_STORE_CLOSE_SYNCING_PRODUCT_DONE_MODAL,
    updateSyncProductDoneUiFlag
  );
  yield takeLatest(
    ONLINE_STORE_SHOW_SYNCING_PRODUCT_IN_SYNC_MODAL,
    pollShopItemSyncStatus
  );
}
